import { motion } from "framer-motion";
import {
  Presentation, FlaskConical, Library, Monitor, BedDouble, Bus, Sparkles, Trophy, Stethoscope,
} from "lucide-react";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const iconMap = { Presentation, FlaskConical, Library, Monitor, BedDouble, Bus, Trophy, Stethoscope };

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Facilities() {
  const { settings } = useSettings();
  const f = settings?.facilities || {};
  const items = f.items || [];

  return (
    <div data-testid="facilities-page">
      <section className="py-20 lg:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{f.eyebrow}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">{f.heading}</h1>
          </motion.div>
          {f.intro && <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5 text-slate-600 text-lg">{f.intro}</motion.p>}
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((item, i) => {
              const Icon = iconMap[item.icon] || Presentation;
              return (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                  data-testid={`facility-card-${i}`}
                  className="group bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1 overflow-hidden"
                >
                  {item.image && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={assetUrl(item.image)}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 sm:p-7">
                    <div
                      className="w-11 h-11 rounded-xl grid place-items-center mb-4"
                      style={{ backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-heading text-xl text-slate-900 leading-snug">{item.title}</h3>
                    <p className="text-slate-600 text-sm mt-3 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {(f.studentLifeHeading || f.studentLifeText) && (
            <motion.div {...fadeUp} className="mt-20 grid lg:grid-cols-12 gap-10 items-center bg-slate-900 rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-grain-dark opacity-30 pointer-events-none" />
              <div className="lg:col-span-7 relative">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-rose-300 mb-4">
                  <Sparkles className="w-3.5 h-3.5" /> Beyond Academics
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">{f.studentLifeHeading}</h2>
                <p className="text-slate-300 text-lg mt-5 leading-relaxed max-w-2xl">{f.studentLifeText}</p>
              </div>
              {items[0]?.image && (
                <div className="lg:col-span-5 relative">
                  <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <img src={assetUrl(items[items.length - 1]?.image || items[0].image)} alt="Students on campus" className="w-full h-72 object-cover" />
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
